import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from '../../../components/BaseComponents/Button';
import SupplementFormModal from './SupplementFormModal/SupplementFormModal';
import { getSupplementsList } from '../../../api/supplements';
import { Supplement } from '../../../api/supplements';
import './SupplementTab.scss';
import SupplementList from './SupplementList/SupplementList';

const SupplementTab: React.FC = () => {
  const [supplements, setSupplements] = useState<Supplement[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supplementToEdit, setSupplementToEdit] = useState<Supplement | null>(null);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const observer = useRef<IntersectionObserver | undefined>(undefined);

  const fetchSupplements = useCallback(async (isInitial: boolean = true, fetchOffset: number = 0) => {
    try {
      if (isInitial) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const currentOffset = isInitial ? 0 : fetchOffset;

      const response = await getSupplementsList(
        undefined,
        undefined,
        undefined,
        limit,
        currentOffset
      );
      const newSupplements = response.supplements || [];

      if (isInitial) {
        setSupplements(newSupplements);
        setTotalCount(response.total_count || 0);
      } else {
        setSupplements(prev => [...prev, ...newSupplements]);
      }

      setOffset(currentOffset + limit);
      setHasMore(newSupplements.length === limit);
    } catch (error) {
      console.error('Error fetching supplements:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [limit]);

  const loadMoreSupplements = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetchSupplements(false, offset);
    }
  }, [isLoadingMore, hasMore, fetchSupplements, offset]);

  const lastSupplementElementRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        loadMoreSupplements();
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore, isLoadingMore, loadMoreSupplements]);

  useEffect(() => {
    fetchSupplements(true);
  }, [fetchSupplements]);

  const handleOpenModal = (editMode: 'create' | 'edit', supplement?: Supplement) => {
    setMode(editMode);
    setSupplementToEdit(supplement || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSupplementToEdit(null);
  };

  const handleSupplementCreated = () => {
    fetchSupplements(true);
  };

  const handleEdit = (supplement: Supplement) => {
    handleOpenModal('edit', supplement);
  };

  const handleSupplementsUpdate = (sortedSupplements: Supplement[]) => {
    console.log('Supplements sorted:', sortedSupplements);
  };

  return (
    <>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='count-container'>
            {totalCount} {totalCount === 1 ? 'supplement' : 'supplements'}
          </div>
        </div>
        <Button
          isSecondary={true}
          text='Add supplement'
          onClick={() => handleOpenModal('create')}
        />
      </div>

      <div className='supplements-container'>
        {isModalOpen && (
          <SupplementFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            supplementToEdit={supplementToEdit}
            mode={mode}
            onSupplementCreated={handleSupplementCreated}
          />
        )}
        <SupplementList
          supplements={supplements}
          isLoading={isLoading}
          onEdit={handleEdit}
          lastSupplementElementRef={lastSupplementElementRef}
          isLoadingMore={isLoadingMore}
          onSupplementsUpdate={handleSupplementsUpdate}
        />
      </div>
    </>
  );
};

export default SupplementTab;
